import React, { useState } from "react";
import { Box, IconButton, Menu, MenuItem, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import TrashIcon from "@icons/myicons/bin-trash-modern-two-line.svg?react";
import SettingsIcon from "@icons/myicons/settings.svg?react";
import ProtectionIcon from "@icons/myicons/password-edit-protection.svg?react";
import {
  CACHE_KEYS,
  useDeleteBankConnectionByProviderV2,
} from "@rentablo/api/apiv1";
import {
  GET_ACCOUNTS,
  GET_BANK_CONNECTION,
  GET_BANK_CONNECTIONS,
} from "@api/cacheKeys";
import { useDeleteStoredSecrets } from "@features/settings/useSettings";
import { ConfirmDeleteDialog } from "@components";
import { SettingsBankConnection } from "@features/settings/SettingsBankConnections/types";

type Props = {
  provider?: "WEALTH_API" | "FIN_API";
  bankConnection?: SettingsBankConnection;
};

export const SettingsBankConnectionMenu = ({
  provider,
  bankConnection,
}: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [menuEl, setMenuEl] = useState<HTMLElement | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const [showPinsDeleteDialog, setShowPinsDeleteDialog] = useState(false);
  const [pinsDeleteDialogError, setPinsDeleteDialogError] = useState<string>();
  const [showBankConnectionDeleteDialog, setShowBankConnectionDeleteDialog] =
    useState(false);
  const [bankConnectionDeleteDialogError, setBankConnectionDeleteDialogError] =
    useState<string>();

  const { mutate: deletePins, isPending } = useDeleteStoredSecrets(
    provider || "FIN_API"
  );
  const { mutate: deleteBankConnection, isPending: isBankConnectionDeleting } =
    useDeleteBankConnectionByProviderV2();
  const handleOpenMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMenuEl(event.currentTarget);
  };
  const handleCloseMenu = () => setMenuEl(null);

  const handleDeletePins = () => {
    if (!bankConnection?.id) return;

    deletePins(
      { id: bankConnection?.id },
      {
        onError: (err) => {
          setPinsDeleteDialogError(
            err.response?.data.message ||
              t("settings.bankConnection.deletePinsModal.error")
          );
        },
        onSuccess: () => {
          setShowPinsDeleteDialog(false);
          handleCloseMenu();
          enqueueSnackbar(
            t("settings.bankConnection.deletePinsModal.success"),
            {
              variant: "success",
              autoHideDuration: 3000,
            }
          );
        },
      }
    );
  };

  const handleDeleteBankConnection = () => {
    if (!bankConnection?.id) return;

    deleteBankConnection(
      { id: bankConnection.id, provider: provider || "FIN_API" },
      {
        onError: (err) => {
          setBankConnectionDeleteDialogError(
            err.response?.data.message ||
              t("settings.bankConnection.deleteBankConnectionModal.error")
          );
        },
        onSuccess: () => {
          setShowBankConnectionDeleteDialog(false);
          handleCloseMenu();
          [
            [CACHE_KEYS.LIST_AND_SEARCH_ALL_BANK_CONNECTIONS_V2],
            [GET_BANK_CONNECTIONS],
            [GET_BANK_CONNECTION],
            [GET_ACCOUNTS],
          ].forEach((queryKey) => queryClient.invalidateQueries({ queryKey }));
          enqueueSnackbar(
            t("settings.bankConnection.deleteBankConnectionModal.success"),
            {
              variant: "success",
              autoHideDuration: 3000,
            }
          );
          navigate("/settings/profile");
        },
      }
    );
  };

  if (!bankConnection) {
    return null;
  }

  return (
    <Box>
      <IconButton onClick={handleOpenMenuClick}>
        <SettingsIcon />
      </IconButton>
      <Menu
        open={Boolean(menuEl)}
        anchorEl={menuEl}
        keepMounted
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem
          data-testid="investment-create"
          onClick={() => setShowPinsDeleteDialog(true)}
        >
          <Box display="flex" alignItems="center" gap={3}>
            <ProtectionIcon width="20px" height="20px" />
            <Typography variant="inherit">
              {t("settings.bankConnection.accounts.menu.deleteSavedPins")}
            </Typography>
          </Box>
        </MenuItem>
        <MenuItem
          data-testid="upload-pdf"
          onClick={() => setShowBankConnectionDeleteDialog(true)}
        >
          <Box display="flex" alignItems="center" gap={3}>
            <TrashIcon width="20px" height="20px" />
            <Typography variant="inherit">
              {t("settings.bankConnection.accounts.menu.deleteBankDetails")}
            </Typography>
          </Box>
        </MenuItem>
      </Menu>
      <ConfirmDeleteDialog
        isOpen={showPinsDeleteDialog}
        title={t("settings.bankConnection.deletePinsModal.title")}
        text={t("settings.bankConnection.deletePinsModal.text")}
        confirmButtonText={t("settings.bankConnection.deletePinsModal.submit")}
        cancelButtonText={t("settings.bankConnection.deletePinsModal.cancel")}
        onClose={() => {
          setShowPinsDeleteDialog(false);
          setPinsDeleteDialogError(undefined);
        }}
        onDelete={handleDeletePins}
        errorMessage={pinsDeleteDialogError}
        isDeleteRequestLoading={isPending}
      />
      <ConfirmDeleteDialog
        isOpen={showBankConnectionDeleteDialog}
        title={t("settings.bankConnection.deleteBankConnectionModal.title")}
        text={t("settings.bankConnection.deleteBankConnectionModal.text")}
        confirmButtonText={t(
          "settings.bankConnection.deleteBankConnectionModal.submit"
        )}
        cancelButtonText={t(
          "settings.bankConnection.deleteBankConnectionModal.cancel"
        )}
        onClose={() => setShowBankConnectionDeleteDialog(false)}
        onDelete={handleDeleteBankConnection}
        errorMessage={bankConnectionDeleteDialogError}
        isDeleteRequestLoading={isBankConnectionDeleting}
      />
    </Box>
  );
};
